import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, MapPin, Warehouse, Store, Archive, Trash2, Loader2 } from "lucide-react";
import { API } from "@/App";

const LOCATION_TYPES = [
  { value: "warehouse", label: "Warehouse", icon: Warehouse },
  { value: "store", label: "Store Floor", icon: Store },
  { value: "basement", label: "Basement/Storage", icon: Archive },
  { value: "online", label: "Online Fulfillment", icon: MapPin },
];

export default function Locations({ user }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    type: "warehouse",
    address: "",
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API}/locations`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (error) {
      toast.error("Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Location name is required");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/locations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Location created");
        setDialogOpen(false);
        setForm({ name: "", type: "warehouse", address: "" });
        fetchLocations();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create location");
      }
    } catch (error) {
      toast.error("Error creating location");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const response = await fetch(`${API}/locations/${deleteId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Location deleted");
        setLocations(locations.filter((l) => l.location_id !== deleteId));
      } else {
        toast.error("Failed to delete location");
      }
    } catch (error) {
      toast.error("Error deleting location");
    }
    setDeleteId(null);
  };

  const getTypeIcon = (type) => {
    const found = LOCATION_TYPES.find((t) => t.value === type);
    return found ? found.icon : MapPin;
  };

  const getTypeLabel = (type) => {
    const found = LOCATION_TYPES.find((t) => t.value === type);
    return found ? found.label : type;
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="locations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Locations
            </h1>
            <p className="text-muted-foreground">
              Manage your inventory locations
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="add-location-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Location
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Location</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Location Name *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Main Warehouse"
                    className="rounded-sm"
                    required
                    data-testid="location-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Location Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="location-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCATION_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address (Optional)</Label>
                  <Input
                    id="address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Street address"
                    className="rounded-sm"
                    data-testid="location-address-input"
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="rounded-sm"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} className="rounded-sm flex-1" data-testid="submit-location-btn">
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Location"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Locations Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="rounded-sm border">
                <CardContent className="p-6">
                  <div className="h-20 skeleton-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : locations.length === 0 ? (
          <Card className="rounded-sm border">
            <CardContent className="p-8 text-center">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">No locations yet</p>
              <Button onClick={() => setDialogOpen(true)} className="rounded-sm">
                Add your first location
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {locations.map((location) => {
              const TypeIcon = getTypeIcon(location.type);
              return (
                <Card key={location.location_id} className="rounded-sm border hover:border-primary/50 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center">
                          <TypeIcon className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-medium">{location.name}</h3>
                          <Badge variant="secondary" className="rounded-sm mt-1">
                            {getTypeLabel(location.type)}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(location.location_id)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`delete-location-${location.location_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {location.address && (
                      <p className="text-sm text-muted-foreground mt-3 pl-15">
                        {location.address}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Location</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this location? This may affect inventory tracking.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
